import {
  CollapsingPathRuleMatch,
  DirectedTriple,
  DirectedTripleWithFiltersWithGraphIds,
  PathCollapsingRule,
  PathCollapsingRuleNonNullablePath,
} from '@ardoq/api-types';
import { countBy, groupBy, uniqBy } from 'lodash';
import {
  assignRegionsToRules,
  getMatchingPaths,
  hasGraphIds,
  keepLongestMatchPerEndPosition,
  processMatches,
} from 'traversals/pathAndGraphUtils';
import { EditablePathCollapsingRule } from 'viewpointBuilder/collapsePathsTab/types';
import {
  PathCollapsingRuleInternal,
  TraversalBuilderState,
  VirtualEdgeState,
} from '../types';
import { toBELabel } from './getPaths';
import { getTraversals } from './getTraversals';

const getCollapsingRuleFilter = (state: TraversalBuilderState) => {
  const allTypeNames = new Set([
    // A node's `modelId` is the component type name, while an edge's composes
    // the whole triple, so the bare reference type comes from the label.
    ...Array.from(state.graphNodes.values()).map(node => node.modelId),
    ...Array.from(state.graphEdges.values()).map(edge =>
      toBELabel(edge.getLabel())
    ),
  ]);
  return ({ sourceType, targetType, referenceType }: DirectedTriple) =>
    allTypeNames.has(sourceType) &&
    allTypeNames.has(targetType) &&
    allTypeNames.has(toBELabel(referenceType));
};

const getAllMatches = (
  state: TraversalBuilderState,
  rule: PathCollapsingRuleNonNullablePath,
  includePathIndexInEqualityCheck = true
): VirtualEdgeState[] => {
  const pathsWithGraphIds = getTraversals(
    state,
    null,
    true
  ) as DirectedTripleWithFiltersWithGraphIds[][];
  const matches = getMatchingPaths(
    pathsWithGraphIds,
    rule,
    includePathIndexInEqualityCheck
  );
  const virtualAndCollapsedEdges = processMatches(state, matches);
  return virtualAndCollapsedEdges;
};

/**
 * True while a recursive rule is standing in for the whole hierarchy: its own
 * edge has replaced every hop the expansion prepended. A rule that keeps levels
 * expanded leaves part of the hierarchy in the dataset, so it does not count.
 * Several rules can collapse the hierarchy, so it is only gone once none of them
 * is showing its full path.
 */
export const isHierarchyCollapsedByRules = (
  rules: PathCollapsingRuleInternal[]
): boolean => {
  const recursiveRules = rules.filter(
    ({ isActive, collapseRecursively }) => isActive && collapseRecursively
  );
  return (
    recursiveRules.length > 0 &&
    recursiveRules.every(
      ({ isExpanded, collapseRecursively }) =>
        !isExpanded && collapseRecursively?.keepExpandedLevels === 0
    )
  );
};

export const getCollapsedPathEdgeIds = (
  rules: PathCollapsingRuleInternal[]
): Set<string> =>
  new Set(
    rules.flatMap(({ virtualEdgeStates }) =>
      virtualEdgeStates.flatMap(({ collapsedEdges, virtualEdge }) => [
        ...collapsedEdges,
        virtualEdge.id,
      ])
    )
  );

export const isHopInCollapsedPath = (
  triple: DirectedTriple,
  collapsedPathEdgeIds: Set<string>
): boolean =>
  hasGraphIds(triple) && collapsedPathEdgeIds.has(triple.referenceGraphId);

export const toInternalPathCollapsingRule = <T extends PathCollapsingRule>(
  state: TraversalBuilderState,
  rule: T,
  isExpanded = false,
  includePathIndexInEqualityCheck = false
): PathCollapsingRuleInternal => {
  return {
    ...rule,
    isExpanded,
    virtualEdgeStates: getAllMatches(
      state,
      rule,
      includePathIndexInEqualityCheck
    ),
  };
};

const typeLevelMatchKey = (
  match: CollapsingPathRuleMatch<DirectedTripleWithFiltersWithGraphIds>
) =>
  `${match.rule.referenceTypeId}-${match.match.rulePathKey}-${match.match.startIndex}-${match.match.start}`;

// Build the virtual-edge states for every rule, resolving overlaps *across*
// rules: when several matches share an end position on the same path they are
// nested, so only the longest survives (see keepLongestMatchPerEndPosition).
// Without this, every overlapping rule would contribute its own virtual edge
// and inflate the edge-removal counts, leaving a tangle of redundant
// "collapsed" edges in the builder graph. Inactive rules keep their standalone
// behaviour - they do not collapse anything, so they must not shadow others.
const getVirtualEdgeStatesByRule = (
  state: TraversalBuilderState,
  rules: PathCollapsingRule[]
): Map<string, VirtualEdgeState[]> => {
  const pathsWithGraphIds = getTraversals(
    state,
    null,
    true
  ) as DirectedTripleWithFiltersWithGraphIds[][];
  const virtualEdgeStatesByRule = new Map<string, VirtualEdgeState[]>();

  // Active rules are resolved together, so a shorter rule's match is dropped
  // when a longer rule collapses the same path further.
  const activeRules = rules.filter(
    rule => rule.isActive && rule.path.length > 0
  );
  const includePathIndexInEqualityCheck = true;
  const withMatchesOverlapValidation = false;
  const perPathMatches = assignRegionsToRules(
    activeRules.flatMap(rule =>
      getMatchingPaths(
        pathsWithGraphIds,
        rule,
        includePathIndexInEqualityCheck,
        withMatchesOverlapValidation
      )
    )
  );
  const survivingMatches = Object.values(
    groupBy(perPathMatches, 'pathIndex')
  ).flatMap(keepLongestMatchPerEndPosition);
  // Collapse the surviving per-path matches back to the type level and recompute
  // the match count = number of paths a given collapsed edge spans.
  const matchCountByKey = countBy(survivingMatches, typeLevelMatchKey);
  const typeLevelMatches = uniqBy(survivingMatches, typeLevelMatchKey).map(
    match => ({
      ...match,
      match: {
        ...match.match,
        matchCount: matchCountByKey[typeLevelMatchKey(match)],
      },
    })
  );
  activeRules.forEach(rule =>
    virtualEdgeStatesByRule.set(rule.referenceTypeId, [])
  );
  Object.entries(
    groupBy(typeLevelMatches, match => match.rule.referenceTypeId)
  ).forEach(([referenceTypeId, matches]) =>
    virtualEdgeStatesByRule.set(referenceTypeId, processMatches(state, matches))
  );

  // Inactive rules are computed standalone (no cross-rule resolution).
  rules
    .filter(rule => !rule.isActive && rule.path.length > 0)
    .forEach(rule =>
      virtualEdgeStatesByRule.set(
        rule.referenceTypeId,
        getAllMatches(state, rule)
      )
    );

  return virtualEdgeStatesByRule;
};

// Recompute virtual-edge states for a set of rules with cross-rule overlap
// resolution, applying a single isExpanded value to all of them.
export const toInternalPathCollapsingRules = <T extends PathCollapsingRule>(
  state: TraversalBuilderState,
  rules: T[],
  isExpanded = false
): PathCollapsingRuleInternal[] => {
  const virtualEdgeStatesByRule = getVirtualEdgeStatesByRule(state, rules);
  return rules.map(rule => ({
    ...rule,
    isExpanded,
    virtualEdgeStates: virtualEdgeStatesByRule.get(rule.referenceTypeId) ?? [],
  }));
};

// Recompute virtual-edge states for the given internal rules with cross-rule
// overlap resolution while preserving each rule's existing isExpanded flag.
export const withResolvedVirtualEdgeStates = (
  state: TraversalBuilderState,
  rules: PathCollapsingRuleInternal[]
): PathCollapsingRuleInternal[] => {
  const virtualEdgeStatesByRule = getVirtualEdgeStatesByRule(state, rules);
  return rules.map(rule => ({
    ...rule,
    virtualEdgeStates: virtualEdgeStatesByRule.get(rule.referenceTypeId) ?? [],
  }));
};

const updateRules = (
  state: TraversalBuilderState,
  pathCollapsingRules: PathCollapsingRule[],
  currentEditTypeId: string | null,
  collapsingRuleFilter: (triple: DirectedTriple) => boolean
) => {
  const isCurrentEditRule = (rule: PathCollapsingRule) =>
    rule.referenceTypeId === currentEditTypeId;
  const isExpanded = false;

  const applicableRules = (pathCollapsingRules || []).filter(
    // Remove any rule which uses triples which are no longer part of the
    // state, we cannot handle them
    rule => isCurrentEditRule(rule) || rule.path.every(collapsingRuleFilter)
  );

  return toInternalPathCollapsingRules(state, applicableRules, isExpanded);
};

const updateCurrentEditableRule = (
  currentEditableRule: EditablePathCollapsingRule | null,
  collapsingRuleFilter: (triple: DirectedTriple) => boolean
) => {
  if (!currentEditableRule) return currentEditableRule;

  return {
    ...currentEditableRule,
    path: currentEditableRule.path.every(collapsingRuleFilter)
      ? currentEditableRule.path
      : [],
  };
};

export const updatePathCollapsingRules = (
  state: TraversalBuilderState,
  pathCollapsingRules: PathCollapsingRule[]
): TraversalBuilderState => {
  const collapsingRuleFilter = getCollapsingRuleFilter(state);
  const { currentEditableRule } = state.pathCollapsing;
  const currentEditTypeId = currentEditableRule?.referenceTypeId ?? null;
  return {
    ...state,
    pathCollapsing: {
      rules: updateRules(
        state,
        pathCollapsingRules,
        currentEditTypeId,
        collapsingRuleFilter
      ),
      currentEditableRule: updateCurrentEditableRule(
        currentEditableRule,
        collapsingRuleFilter
      ),
    },
  };
};

// The viewpoint builder graph is essentially a tree, represented by paths from
// the root to the leaves. Each edge in the graph has a unique ID. Collapsing
// rules specify paths to be collapsed using this notation.
// Collapsed edges are those that match a collapsing rule against the entire
// graph's paths. To determine if a collapsed edge can be removed, we count its
// occurrences in the entire graph's paths and compare it with its occurrences
// in all collapsed paths. If both counts are the same, indicating the edge
// appears the same number of times in the entire graph and in all collapsed
// paths, we can safely remove it.
export const updateEdgesToBeRemovedOnCollapsing = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  const pathsWithGraphIds = (
    getTraversals(
      state,
      null,
      true
    ) as DirectedTripleWithFiltersWithGraphIds[][]
  ).flatMap(path => path.map(({ referenceGraphId }) => referenceGraphId));
  const countAllIndex = countBy(pathsWithGraphIds);
  const collapsingRules = state.pathCollapsing.rules;
  const collapsedEdges = collapsingRules.flatMap(rule =>
    rule.isExpanded || !rule.isActive
      ? []
      : rule.virtualEdgeStates.flatMap(({ collapsedEdges, matchCount }) =>
          Array(matchCount).fill(collapsedEdges).flat()
        )
  );
  const countIndex = countBy(collapsedEdges);
  const edgesToBeRemoved = new Set(
    collapsedEdges.filter(id => countIndex[id] === countAllIndex[id])
  );
  const updatedCollapsingRules = collapsingRules.map(rule => {
    const virtualEdgeStates = rule.virtualEdgeStates.map(virtualEdgeState => {
      const edgesToBeRemovedOnCollapsing =
        virtualEdgeState.collapsedEdges.filter(id => edgesToBeRemoved.has(id));
      return {
        ...virtualEdgeState,
        edgesToBeRemovedOnCollapsing,
      };
    });
    return {
      ...rule,
      virtualEdgeStates,
    };
  });

  return {
    ...state,
    pathCollapsing: {
      ...state.pathCollapsing,
      rules: updatedCollapsingRules,
    },
  };
};
